import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";

const WeeklyHoursWidget = ({ globalMode = false }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserSelector, setShowUserSelector] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [showTrendTooltip, setShowTrendTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, right: 0 });
    const [barTooltip, setBarTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
    const infoIconRef = React.useRef(null);
    const activityMatrixRef = React.useRef(null);
    const Auth = useAuthentication();
    const CurrentUser = useSelector((state) => state.CurrentUser);
    const Team = useSelector((state) => state.Team);

    const isAdmin = CurrentUser.data && CurrentUser.data.role === 'administrator';

    useEffect(() => {
        loadStats();
    }, [selectedUser, globalMode]);

    useEffect(() => {
        // Scroll to end (right) when stats are loaded
        if (stats && activityMatrixRef.current) {
            activityMatrixRef.current.scrollLeft = activityMatrixRef.current.scrollWidth;
            updateScrollButtons();
        }
    }, [stats]);

    const updateScrollButtons = () => {
        if (activityMatrixRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = activityMatrixRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const scrollPage = (direction) => {
        if (activityMatrixRef.current) {
            const scrollAmount = activityMatrixRef.current.clientWidth * 0.8;
            activityMatrixRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
            setTimeout(updateScrollButtons, 300);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showUserSelector && !event.target.closest('.position-relative')) {
                setShowUserSelector(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showUserSelector]);

    const loadStats = () => {
        setLoading(true);
        const params = new URLSearchParams({ weeks: 260 }); // ~5 Jahre = 260 Wochen
        
        // In global mode, don't send user parameter to get all users' stats
        // In personal mode, send selected user or no parameter for own stats
        if (!globalMode && selectedUser) {
            params.append('user', selectedUser);
        }
        
        // If in global mode, we want all users' combined stats
        if (globalMode) {
            params.append('all_users', 'true');
        }

        axios({
            method: "get",
            url: SyncConstants["syncBaseURL"] + "expenseEntries/userWeeklyStats?" + params.toString(),
            auth: Auth.getAuthObject()
        }).then(response => {
            if (response.data.success) {
                setStats(response.data.data);
            }
            setLoading(false);
        }).catch(error => {
            console.error("Error loading weekly stats:", error);
            setLoading(false);
        });
    };

    if (loading) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Laden...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <p className="text-muted">Keine Daten verfügbar</p>
                </div>
            </div>
        );
    }

    // Constants for last 16 weeks only
    const maxHoursPerWeek = 40; // Maximale Höhe = 40h pro Woche
    const last16Weeks = stats.weekly_data.slice(-16);

    // Calculate averages for visible 16 weeks only (considering only weekdays Mon-Fri)
    const totalMinutesLast16 = last16Weeks.reduce((sum, week) => sum + week.total_minutes, 0);

    // Count ALL weekdays (Mon-Fri) in the 16 week period, including days without bookings
    // Dynamically count actual weekdays from the data
    const totalWeekdaysLast16 = last16Weeks.reduce((sum, week) => {
        // Count how many weekdays (Mon-Fri) exist in this week's data
        // week.days contains all 7 days, indices 0-4 are Mon-Fri
        const weekdaysInWeek = week.days.slice(0, 5).length;
        return sum + weekdaysInWeek;
    }, 0);

    // Average per day = total minutes / all weekdays (including days without bookings)
    const avgHoursPerDay = totalWeekdaysLast16 > 0 ? (totalMinutesLast16 / totalWeekdaysLast16 / 60) : 0;

    // Average per week = total minutes / number of weeks
    const avgHoursPerWeek = last16Weeks.length > 0 ? (totalMinutesLast16 / last16Weeks.length / 60) : 0;

    // Determine colors based on thresholds
    const getDayColor = (hours) => {
        if (hours < 4) return { bg: 'rgba(220, 53, 69, 0.08)', border: 'rgba(220, 53, 69, 0.15)', text: 'text-danger' }; // Red
        if (hours < 5) return { bg: 'rgba(255, 193, 7, 0.08)', border: 'rgba(255, 193, 7, 0.15)', text: 'text-warning' }; // Yellow
        return { bg: 'rgba(13, 110, 253, 0.08)', border: 'rgba(13, 110, 253, 0.15)', text: 'text-primary' }; // Blue
    };

    const getWeekColor = (hours) => {
        if (hours < 20) return { bg: 'rgba(220, 53, 69, 0.08)', border: 'rgba(220, 53, 69, 0.15)', text: 'text-danger' }; // Red
        if (hours < 25) return { bg: 'rgba(255, 193, 7, 0.08)', border: 'rgba(255, 193, 7, 0.15)', text: 'text-warning' }; // Yellow
        return { bg: 'rgba(25, 135, 84, 0.08)', border: 'rgba(25, 135, 84, 0.15)', text: 'text-success' }; // Green
    };

    const dayColor = getDayColor(avgHoursPerDay);
    const weekColor = getWeekColor(avgHoursPerWeek);

    // Calculate trend: Compare last 5 working days vs. overall average
    const getLast5WorkdaysMinutes = () => {
        // Get all daily data and filter for last 5 weekdays with or without bookings
        const allDays = stats.daily_data;
        let workdaysFound = 0;
        let totalMinutes = 0;

        // Go backwards through days to find last 5 weekdays
        for (let i = allDays.length - 1; i >= 0 && workdaysFound < 5; i--) {
            const date = new Date(allDays[i].date);
            const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

            // Check if it's a weekday (Monday = 1 to Friday = 5)
            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                totalMinutes += allDays[i].minutes;
                workdaysFound++;
            }
        }

        return totalMinutes;
    };

    const last5WorkdaysMinutes = getLast5WorkdaysMinutes();
    const last5WorkdaysAvgPerDay = last5WorkdaysMinutes / 5 / 60; // Average hours per day in last 5 days
    const overallAvgPerDay = avgHoursPerDay; // Overall average from 16 weeks

    // Calculate trend percentage
    const trendPercentage = overallAvgPerDay > 0
        ? ((last5WorkdaysAvgPerDay - overallAvgPerDay) / overallAvgPerDay) * 100
        : 0;

    // Determine trend message
    const getTrendMessage = () => {
        const absDiff = Math.abs(trendPercentage);

        if (absDiff < 5) {
            // Stable (within 5%)
            return {
                text: "Deine Buchungen sind stabil! Du hältst dein Niveau konstant - weiter so!",
                icon: "bi-graph-up",
                color: "text-primary"
            };
        } else if (trendPercentage > 15) {
            // Strong increase
            return {
                text: "Super, es wird immer besser! Deine Aufwandsbuchungen steigen unaufhaltsam.",
                icon: "bi-rocket-takeoff",
                color: "text-success"
            };
        } else if (trendPercentage > 5) {
            // Moderate increase
            return {
                text: "Positiver Trend! Deine Buchungen nehmen zu - du bist auf einem guten Weg!",
                icon: "bi-arrow-up-circle",
                color: "text-success"
            };
        } else if (trendPercentage < -15) {
            // Strong decrease
            return {
                text: "In den letzten fünf Arbeitstagen hast du gemessen an deinem sonstigen Durchschnitt weniger Stunden gebucht. Achte darauf, die Buchungen nicht einreissen zu lassen! Gute Arbeit muss auch vom Kunden gewürdigt werden.",
                icon: "bi-exclamation-triangle",
                color: "text-danger"
            };
        } else {
            // Moderate decrease
            return {
                text: "Deine Buchungen sind leicht rückläufig. Versuche wieder an dein übliches Niveau anzuknüpfen.",
                icon: "bi-arrow-down-circle",
                color: "text-warning"
            };
        }
    };

    const trendMessage = getTrendMessage();

    // Get all team members for dropdown (exclude admins)
    const teamMembers = Team ? Object.keys(Team)
        .filter(username => Team[username].role !== 'administrator')
        .map(username => ({
            username,
            realName: Team[username].realName
        }))
        .sort((a, b) => a.realName.localeCompare(b.realName)) : [];

    const getDisplayName = () => {
        if (globalMode) return "Team-Übersicht";
        if (!selectedUser) return "Meine Übersicht";
        const user = Team[selectedUser];
        return user ? `${user.realName}s Übersicht` : `${selectedUser}s Übersicht`;
    };

    return (
        <>
            {/* Trend Tooltip */}
            {showTrendTooltip && (
                <div
                    className="position-fixed text-white p-3 rounded-3 shadow-lg"
                    style={{
                        top: `${tooltipPosition.top}px`,
                        right: `${tooltipPosition.right}px`,
                        minWidth: '250px',
                        fontSize: '0.75rem',
                        zIndex: 99999,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        background: 'rgba(33, 37, 41, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                >
                    <div className="fw-bold mb-2">Berechnungsgrundlage:</div>
                    <div>Letzten 5 Arbeitstage: <strong>{last5WorkdaysAvgPerDay.toFixed(1)}h/Tag</strong></div>
                    <div>Gesamtdurchschnitt (16 Wochen): <strong>{overallAvgPerDay.toFixed(1)}h/Tag</strong></div>
                    <div className="mt-2 pt-2 border-top border-secondary">
                        Trend: <strong className={trendMessage.color}>{trendPercentage > 0 ? '+' : ''}{trendPercentage.toFixed(1)}%</strong>
                    </div>
                </div>
            )}

            {/* Bar Tooltip */}
            {barTooltip.show && (
                <div
                    className="position-fixed text-white p-2 rounded-3 shadow-lg"
                    style={{
                        top: `${barTooltip.y}px`,
                        left: `${barTooltip.x}px`,
                        fontSize: '0.75rem',
                        zIndex: 99999,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        background: 'rgba(33, 37, 41, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transform: 'translate(-50%, -120%)'
                    }}
                    dangerouslySetInnerHTML={{ __html: barTooltip.content }}
                />
            )}

            <div className="card h-100" style={{
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.08)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                background: 'rgba(255, 255, 255, 0.95)'
            }}>
                <div className="card-body">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">
                        <i className="bi bi-graph-up me-2 text-primary"></i>
                        {getDisplayName()} (16 Wochen)
                    </h5>
                    {isAdmin && !globalMode && (
                        <div className="position-relative">
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setShowUserSelector(!showUserSelector)}
                                title="Benutzer wechseln"
                            >
                                <i className="bi bi-person-gear"></i>
                            </button>
                            {showUserSelector && (
                                <div className="position-absolute end-0 mt-2 bg-white border rounded shadow-sm" style={{ minWidth: '200px', zIndex: 1000 }}>
                                    <div className="p-2">
                                        <div className="small text-muted mb-2 px-2">Benutzer auswählen:</div>
                                        <div className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            <button
                                                className={`list-group-item list-group-item-action small ${!selectedUser ? 'active' : ''}`}
                                                onClick={() => {
                                                    setSelectedUser(null);
                                                    setShowUserSelector(false);
                                                }}
                                            >
                                                <i className="bi bi-person-circle me-2"></i>
                                                Meine Statistik
                                            </button>
                                            {teamMembers.map(member => (
                                                <button
                                                    key={member.username}
                                                    className={`list-group-item list-group-item-action small ${selectedUser === member.username ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setSelectedUser(member.username);
                                                        setShowUserSelector(false);
                                                    }}
                                                >
                                                    <i className="bi bi-person me-2"></i>
                                                    {member.realName}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Summary Stats */}
                <div className="row g-2 mb-4">
                    <div className="col-6">
                        <div className="text-center p-3 rounded" style={{
                            animation: 'fadeInUp 0.5s ease-out',
                            animationDelay: '0.1s',
                            animationFillMode: 'both',
                            background: dayColor.bg,
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${dayColor.border}`
                        }}>
                            <div className={`h2 mb-0 ${dayColor.text} fw-bold`} style={{
                                animation: 'countUp 0.8s ease-out',
                                animationDelay: '0.3s',
                                animationFillMode: 'both'
                            }}>
                                {avgHoursPerDay.toFixed(1)}h
                            </div>
                            <small className="text-muted">Ø pro Tag</small>
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="text-center p-3 rounded" style={{
                            animation: 'fadeInUp 0.5s ease-out',
                            animationDelay: '0.2s',
                            animationFillMode: 'both',
                            background: weekColor.bg,
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${weekColor.border}`
                        }}>
                            <div className={`h2 mb-0 ${weekColor.text} fw-bold`} style={{
                                animation: 'countUp 0.8s ease-out',
                                animationDelay: '0.4s',
                                animationFillMode: 'both'
                            }}>
                                {avgHoursPerWeek.toFixed(1)}h
                            </div>
                            <small className="text-muted">Ø pro Woche</small>
                        </div>
                    </div>
                </div>

                {/* Trend Message */}
                <div className="mb-4" style={{
                    animation: 'fadeInUp 0.5s ease-out',
                    animationDelay: '0.3s',
                    animationFillMode: 'both'
                }}>
                    <div className="p-3 rounded d-flex align-items-start position-relative" style={{
                        background: 'rgba(248, 249, 250, 0.6)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0, 0, 0, 0.06)'
                    }}>
                        <i className={`${trendMessage.icon} me-2 ${trendMessage.color}`} style={{ fontSize: '1.2rem', marginTop: '2px' }}></i>
                        <div className="small flex-grow-1">{trendMessage.text}</div>
                        <div className="position-relative ms-2">
                            <i
                                ref={infoIconRef}
                                className="bi bi-info-circle text-muted"
                                style={{ fontSize: '1rem', cursor: 'help' }}
                                onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setTooltipPosition({
                                        top: rect.bottom + 10,
                                        right: window.innerWidth - rect.right
                                    });
                                    setShowTrendTooltip(true);
                                }}
                                onMouseLeave={() => setShowTrendTooltip(false)}
                            ></i>
                        </div>
                    </div>
                </div>

                <style>{`
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    @keyframes countUp {
                        from {
                            opacity: 0;
                            transform: scale(0.5);
                        }
                        to {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                    @keyframes growUp {
                        from {
                            height: 0 !important;
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }
                `}</style>

                {/* Weekly Bars Chart - last 16 weeks */}
                <div className="mb-4">
                    <h6 className="mb-3 small text-muted">Wochenübersicht (letzte 16 Wochen)</h6>
                    <div className="d-flex align-items-end justify-content-between" style={{ height: '120px', gap: '3px' }}>
                        {stats.weekly_data.slice(-16).map((week, index) => {
                            const heightPercent = (week.total_hours / maxHoursPerWeek) * 100;
                            const isAboveAverage = week.total_hours > avgHoursPerWeek;

                            return (
                                <div
                                    key={week.week}
                                    className="d-flex flex-column justify-content-end align-items-center flex-grow-1"
                                    style={{ height: '100%' }}
                                >
                                    <div
                                        className="w-100 rounded-3 position-relative overflow-hidden"
                                        style={{
                                            height: `${Math.max(heightPercent, 2)}%`,
                                            backgroundColor: isAboveAverage ? 'rgba(25, 135, 84, 0.15)' : 'rgba(211, 47, 47, 0.15)',
                                            backdropFilter: 'blur(10px)',
                                            border: `1px solid ${isAboveAverage ? 'rgba(25, 135, 84, 0.3)' : 'rgba(211, 47, 47, 0.3)'}`,
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            animation: `growUp 0.6s ease-out ${index * 0.05}s both`
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = isAboveAverage ? 'rgba(25, 135, 84, 0.25)' : 'rgba(211, 47, 47, 0.25)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';

                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setBarTooltip({
                                                show: true,
                                                content: `<div class="fw-bold">KW ${week.week_number}</div><div>${week.total_hours.toFixed(1)}h gesamt</div><div class="text-muted small">Ø ${week.average_hours_per_day.toFixed(1)}h/Tag</div>`,
                                                x: rect.left + rect.width / 2,
                                                y: rect.top
                                            });
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = isAboveAverage ? 'rgba(25, 135, 84, 0.15)' : 'rgba(211, 47, 47, 0.15)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            setBarTooltip({ show: false, content: '', x: 0, y: 0 });
                                        }}
                                    >
                                        {/* Subtiler Glanz */}
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: '50%',
                                            background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                                            pointerEvents: 'none'
                                        }}></div>
                                    </div>
                                    <small className="text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                        KW {week.week_number}
                                    </small>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* GitHub-style Activity Matrix - all time */}
                <div className="position-relative">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 small text-muted">
                            Gesamte Aktivität
                            <small className="text-muted ms-2">({stats.daily_data.length} Tage)</small>
                        </h6>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => scrollPage('left')}
                                disabled={!canScrollLeft}
                                style={{ padding: '2px 8px' }}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => scrollPage('right')}
                                disabled={!canScrollRight}
                                style={{ padding: '2px 8px' }}
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                    <div
                        ref={activityMatrixRef}
                        className="d-flex"
                        style={{
                            gap: '3px',
                            overflowX: 'hidden',
                            paddingBottom: '5px',
                            scrollBehavior: 'smooth'
                        }}
                        onScroll={updateScrollButtons}
                    >
                        {(() => {
                            // Get all data
                            const allData = stats.daily_data;

                            // Group by weeks (starting Monday)
                            const weekGroups = [];
                            let currentWeek = [];

                            // Start from the first Monday
                            let startIndex = 0;
                            const firstDate = new Date(allData[0].date);
                            const firstDayOfWeek = firstDate.getDay();
                            const adjustedFirstDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

                            // Skip to next Monday if not starting on Monday
                            if (adjustedFirstDay !== 0) {
                                startIndex = 7 - adjustedFirstDay;
                            }

                            allData.slice(startIndex).forEach((day, index) => {
                                const dayOfWeek = new Date(day.date).getDay();
                                const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Mo=0, So=6

                                // Start new week on Monday
                                if (currentWeek.length > 0 && adjustedDay === 0) {
                                    weekGroups.push(currentWeek);
                                    currentWeek = [];
                                }

                                currentWeek.push(day);

                                // Last day
                                if (index === allData.slice(startIndex).length - 1 && currentWeek.length > 0) {
                                    weekGroups.push(currentWeek);
                                }
                            });

                            return (
                                <>
                                    {weekGroups.map((week, weekIndex) => {
                                        // Show month label every ~4 weeks
                                        const showMonthLabel = weekIndex === 0 || (weekIndex % 4 === 0 && week[0]);
                                        const monthLabel = showMonthLabel && week[0]
                                            ? new Date(week[0].date).toLocaleDateString('de-DE', { month: 'short' })
                                            : null;

                                        return (
                                            <div key={weekIndex} className="d-flex flex-column position-relative" style={{ gap: '3px' }}>
                                                {monthLabel && (
                                                    <div
                                                        className="position-absolute text-muted"
                                                        style={{
                                                            fontSize: '0.65rem',
                                                            top: '-18px',
                                                            left: '0',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {monthLabel}
                                                    </div>
                                                )}
                                                {Array.from({ length: 7 }).map((_, dayIndex) => {
                                                    const day = week[dayIndex];

                                                    if (!day) {
                                                        return <div key={dayIndex} style={{ width: '10px', height: '10px' }}></div>;
                                                    }

                                                    const dayOfWeek = new Date(day.date).getDay();
                                                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                                                    const intensity = Math.min(day.hours / 8, 1);
                                                    let bgColor = '#ebedf0';

                                                    if (!isWeekend && day.hours > 0) {
                                                        if (intensity <= 0.25) bgColor = '#ffcdd2';
                                                        else if (intensity <= 0.5) bgColor = '#ef9a9a';
                                                        else if (intensity <= 0.75) bgColor = '#e57373';
                                                        else bgColor = '#c62828';
                                                    }

                                                    return (
                                                        <div
                                                            key={day.date}
                                                            className="rounded-1"
                                                            style={{
                                                                width: '10px',
                                                                height: '10px',
                                                                backgroundColor: bgColor,
                                                                cursor: 'pointer',
                                                                border: '1px solid rgba(0,0,0,0.05)',
                                                                transition: 'all 0.2s ease',
                                                                transform: 'scale(1)'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.transform = 'scale(1.3)';
                                                                e.currentTarget.style.zIndex = '10';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.transform = 'scale(1)';
                                                                e.currentTarget.style.zIndex = '1';
                                                            }}
                                                            title={`${day.date}: ${day.hours.toFixed(1)}h`}
                                                        ></div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </>
                            );
                        })()}
                    </div>

                    {/* Legend */}
                    <div className="d-flex justify-content-end align-items-center mt-3" style={{ gap: '5px' }}>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>Weniger</small>
                        {['#ebedf0', '#ffcdd2', '#ef9a9a', '#e57373', '#c62828'].map((color, i) => (
                            <div
                                key={i}
                                className="rounded-1"
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: color,
                                    border: '1px solid rgba(0,0,0,0.05)'
                                }}
                            ></div>
                        ))}
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>Mehr</small>
                    </div>
                </div>

                {/* Total Summary */}
                <div className="mt-3 pt-3 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                            <i className="bi bi-clock-history me-1"></i>
                            Gesamt: <strong>{stats.summary.total_hours.toFixed(1)}h</strong>
                        </small>
                        <small className="text-muted">
                            {stats.summary.total_weeks} Wochen
                        </small>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default WeeklyHoursWidget;
